import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { Button, Checkbox } from "north.js";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

import { errorToast, successToast } from "src/shared/components/Toast";

import { PRIMARY_ROLES } from "../../shift-schedule/constants";
import { useUpdateVolunteerMutation } from "../graphql/updateVolunteer.generated";
import { Volunteer } from "../index";

interface EditVolunteerProps {
  volunteer: Volunteer;
}

const EditVolunteer = ({ volunteer }: EditVolunteerProps) => {
  const [primaryRole, setPrimaryRole] = useState<string>(volunteer.primaryRole);
  const [checked, setChecked] = useState<boolean>(
    volunteer.additionalRoles.length > 0
  );

  const [updateVolunteerMutation] = useUpdateVolunteerMutation();

  const updateVolunteer = useCallback(async () => {
    try {
      await updateVolunteerMutation({
        variables: {
          primary_category_name: primaryRole,
          min_primary_signups: checked ? 0 : volunteer.minPrimarySignups,
          event_attendee_id: volunteer.eventAttendeeId,
        },
      });
      successToast("Changes saved!");
    } catch (e) {
      if (e instanceof Error) {
        errorToast(e.message);
      } else {
        errorToast("An unknown error occurred");
      }
    }
  }, [primaryRole, checked, volunteer, updateVolunteerMutation]);

  return (
    <InfoContainer>
      <BackLink href={"/volunteer/volunteers"}>
        <ArrowLeftIcon width="16px" height="16px" />
        <BackText>Back to Volunteers</BackText>
      </BackLink>
      <Heading2>Edit a Volunteer</Heading2>
      <Info>
        <LargeBodyBold>Full Name</LargeBodyBold>
        <Body>{volunteer.name}</Body>
      </Info>
      <Info>
        <LargeBodyBold>Email</LargeBodyBold>
        <Body>{volunteer.email}</Body>
      </Info>
      <Info>
        <LargeBodyBold>Roles</LargeBodyBold>
        <RoleSection>
          <Body>Primary Role:</Body>
          <RoleCardsContainer>
            {PRIMARY_ROLES.map((role) => (
              <SelectTag
                key={role}
                selected={primaryRole === role}
                onClick={() => setPrimaryRole(role)}
              >
                {role}
              </SelectTag>
            ))}
          </RoleCardsContainer>
        </RoleSection>
        <Checkbox
          disabled={checked}
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          label="Allow volunteers to bypass 2 shifts rule"
        />
      </Info>
      <Button color="primary" onClick={updateVolunteer}>
        Save Changes
      </Button>
    </InfoContainer>
  );
};

export default EditVolunteer;

const BackLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  text-decoration: none;
`;

const BackText = styled.p`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const InfoContainer = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RoleSection = styled.div`
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RoleCardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const SelectTag = styled.button<{ selected: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid
    ${(props) =>
      props.selected
        ? props.theme.globalConstants.color.borderPrimary
        : props.theme.globalConstants.color.borderGray};
  background: ${(props) =>
    props.selected
      ? props.theme.globalConstants.color.backgroundPrimaryLight
      : props.theme.globalConstants.color.white};
  color: ${(props) =>
    props.selected
      ? props.theme.globalConstants.color.textPrimary
      : props.theme.globalConstants.color.textSecondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;

  &:hover {
    border: 1px solid
      ${({ theme }) => theme.globalConstants.color.borderPrimary};
  }
`;

const Heading2 = styled.h2`
  color: var(--North-Text-Body, #1f2937);
  font-size: 26px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
`;

const LargeBodyBold = styled.p`
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%;
`;

const Body = styled.p`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 180%;
`;
